/**
 * Copy detection — one recompute per exam, once the whole grading wave has drained.
 *
 * GATED ON THE EXAM-WIDE SIGNAL (`examState.gradingProgress.status === 'COMPLETED'`), not on the `grading` STEP
 * itself: `gradingStep.finish` (see `steps/grading.step.ts`) only fires once every row of all three grading
 * phases (content, language-use, consistency) is drained, and that is exactly "this exam's grades are final for
 * now" — the only moment a copy check is worth running. Deliberately NOT joining `EXTRA_STEP_TYPES.grading`
 * itself: this isn't one of the three phases that produce a grade, and sharing that step would make ITS
 * completion re-trigger `closeGradingAndAnnounce`'s "Bewertung abgeschlossen" announcement a second time.
 * Absent from every step map, this type gets its own solo batch scope for free (`batchScopeOf` falls back to
 * the type itself), so it still gets normal dedupe/lock bookkeeping without touching grading's semantics.
 *
 * `dedupeUntil: 'claimed'`, matching `grading` rather than `gradingConsistency`: this job's OUTPUT depends on
 * every submission's current answers and grades, read fresh each run — not just on its (empty) payload. Joining
 * an already-CLAIMED run would silently apply a newer regrade's completion to an older computation's output.
 * A PENDING twin still absorbs everything, so a burst of regrades before the first row claims still produces
 * one recompute, not one per regrade.
 */

import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { CopyDetectionResultRecord, toCopyDetectionResultRecord } from '../../../copy-detection-result.js';
import { resolveAnswerTexts } from '../../../utils/copy-detection-answers.js';
import { buildCopyDetectionResult } from '../../../utils/copy-detection.js';
import { getSubmissionsForExam } from '../../../utils/submission-tree.js';
import { defineJob } from '../registry.js';
import type { GradingScope } from './grading.job.js';

export interface CopyDetectionPayload {
  [key: string]: unknown;
}

export const copyDetectionJob = defineJob<CopyDetectionPayload>({
  type: 'copyDetection',

  batch: {
    units: (_examId, scope) => {
      const { target } = scope as GradingScope;
      // A languageUse-only regrade writes no content answers — nothing new for copy detection to compare.
      if ((target ?? 'content') === 'languageUse') return [];
      return [{}];
    },
    joinTwin: true,
    waitMs: 5 * 60_000,
  },

  lockKey: examId => `exam:${examId}:copyDetection`,
  dedupeKey: examId => `copyDetection:${examId}`,
  dedupeUntil: 'claimed',

  ready: exam => {
    const status = (exam as any)?.examState?.gradingProgress?.status;
    return status === 'COMPLETED' ? 'run' : { verdict: 'wait', because: 'copyDetection:grading-not-finished' };
  },

  // Image-only answers (calculation, proof, ...) are transcribed here on first run — one OCR call per crop, cached
  // by crop id, so a regrade's recompute costs nothing extra.
  maxRuntimeMs: 15 * 60_000,
  maxAttempts: 2,

  handler: async (examId, _payload, context) => {
    const submissions = await getSubmissionsForExam(examId);
    if (submissions.length < 2) {
      return { skipped: true, note: 'fewer than two submissions — nothing to compare' };
    }

    const { texts, transcribedCount, cost } = await resolveAnswerTexts(context.exam, submissions, context.signal);
    const result = buildCopyDetectionResult(context.exam as any, submissions, {}, texts);
    const record = toCopyDetectionResultRecord(result);

    const rawResultCollection = await getRawCollection(CopyDetectionResultRecord._collectionName);
    await rawResultCollection.deleteMany({ examId });
    const resultCollection = await getCollection(CopyDetectionResultRecord);
    await resultCollection.insertOne(record.toObject() as any);

    return {
      note: `copy detection: ${result.pairs.length} pairs scored, ${result.flaggedPairIds.length} flagged, ${transcribedCount} answers transcribed (cost ${cost.toFixed(4)})`,
      result: {
        pairCount: result.pairs.length,
        flaggedCount: result.flaggedPairIds.length,
        transcribedCount,
        transcriptionCost: cost,
      },
    };
  },
});
